import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { fetchExtraordinaryMovements, reviewExtraordinaryMovement } from "../services/payrollService";
import { useDeals } from "../../crm/services/dealsService";
import { fetchLeads, type Lead } from "../../crm/services/leadsService";
import { buildThemeClasses, formatMoney } from "../theme";
import type {
  ExtraordinaryClassification,
  ExtraordinaryMemberTotal,
  ExtraordinaryMovement,
} from "../types";

// ---------------------------------------------------------------------------
// Movimientos extraordinarios (#1376).
//
// Todo pago a un colaborador que NO es un pago normal de quincena: no es
// sueldo, o el monto no es el de su sueldo. El servidor los detecta cruzando
// los pagos de nómina y los gastos de nómina contra el padrón; aquí se listan,
// se filtran por persona y quien administra los clasifica (anticipo, préstamo,
// descuento, bono…) o los marca como sueldo normal para sacarlos de la lista.
//
// El padrón guarda el sueldo ACTUAL: tras un aumento, los pagos anteriores
// aparecen como «monto distinto». Para eso está «sueldo normal», y «sueldo
// normal siempre que sea este monto» cuando a alguien se le paga una parte fija
// por fuera (un cliente le deposita directo el resto).
// ---------------------------------------------------------------------------

// #1491: «reembolso» (lo que el colaborador puso por un cliente) se puede
// cargar a un negocio o lead del CRM y cuenta en el costo de ese negocio.
const CLASSIFICATIONS: ExtraordinaryClassification[] = ["advance", "loan", "deduction", "bonus", "reimbursement", "other", "regular"];

interface Props {
  /** Quien administra clasifica; los demás ven lo suyo en solo lectura. */
  canReview: boolean;
  /** #1487: «Registrar como aumento de sueldo» en un pago de sueldo con monto distinto. */
  onRegisterRaise?: (row: ExtraordinaryMovement) => void;
}

export default function ExtraordinaryMovements({ canReview, onRegisterRaise }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [items, setItems] = useState<ExtraordinaryMovement[]>([]);
  const [members, setMembers] = useState<ExtraordinaryMemberTotal[]>([]);
  const [summary, setSummary] = useState({ count: 0, unclassified: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberId, setMemberId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeRegular, setIncludeRegular] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // La persona se filtra aquí, no en el servidor: así el selector conserva a todos.
      const res = await fetchExtraordinaryMovements({ from, to, includeRegular });
      setItems(res.items ?? []);
      setMembers(res.members ?? []);
      setSummary(res.summary ?? { count: 0, unclassified: 0 });
    } catch {
      setError(t("payroll.extraordinary.loadError"));
    } finally {
      setLoading(false);
    }
  }, [from, to, includeRegular, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const classify = async (row: ExtraordinaryMovement, value: string) => {
    setSaving(row.key);
    setError(null);
    try {
      await reviewExtraordinaryMovement({
        source: row.source,
        sourceId: row.sourceId,
        classification: (value || null) as ExtraordinaryClassification | null,
        note: row.note,
      });
      await load();
    } catch {
      setError(t("payroll.extraordinary.saveError"));
    } finally {
      setSaving(null);
    }
  };

  // #1487: nota del porqué (editable en lo clasificado) con historial.
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const noteEditable = (row: ExtraordinaryMovement) =>
    canReview && Boolean(row.classification) && row.classification !== "regular" && row.classification !== "regular_amount";
  const draftOf = (row: ExtraordinaryMovement) => noteDrafts[row.key] ?? row.note ?? "";

  const saveNote = async (row: ExtraordinaryMovement) => {
    const text = draftOf(row).trim();
    setSaving(row.key);
    setError(null);
    try {
      await reviewExtraordinaryMovement({
        source: row.source,
        sourceId: row.sourceId,
        classification: row.classification === "regular_amount" ? null : row.classification,
        note: text || null,
      });
      setNoteDrafts((d) => {
        const next = { ...d };
        delete next[row.key];
        return next;
      });
      await load();
    } catch {
      setError(t("payroll.extraordinary.saveError"));
    } finally {
      setSaving(null);
    }
  };

  // #1491: a qué negocio o lead se carga un reembolso. Los negocios salen del
  // almacén compartido del CRM; los leads se piden una vez al haber un reembolso.
  const deals = useDeals();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const hasReimbursement = canReview && items.some((i) => i.classification === "reimbursement");
  useEffect(() => {
    if (!hasReimbursement || leadsLoaded) return;
    setLeadsLoaded(true);
    fetchLeads({ limit: 200 })
      .then((page) => setLeads(page.data ?? []))
      .catch(() => setLeads([]));
  }, [hasReimbursement, leadsLoaded]);

  const assignTarget = async (row: ExtraordinaryMovement, target: { dealId?: string | null; leadId?: string | null }) => {
    setSaving(row.key);
    setError(null);
    try {
      await reviewExtraordinaryMovement({
        source: row.source,
        sourceId: row.sourceId,
        classification: "reimbursement",
        note: row.note,
        dealId: target.dealId === undefined ? row.dealId ?? null : target.dealId,
        leadId: target.leadId === undefined ? row.leadId ?? null : target.leadId,
      });
      await load();
    } catch {
      setError(t("payroll.extraordinary.saveError"));
    } finally {
      setSaving(null);
    }
  };
  const leadLabel = (l: Lead) => [l.name, l.company].filter(Boolean).join(" · ") || l.id;

  const visible = memberId ? items.filter((i) => i.teamMemberId === memberId) : items;
  const inputCls = `rounded-lg border px-2 py-1.5 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;
  const th = `px-3 py-2 text-left text-xs font-medium ${tc.textSecondary}`;
  const td = `px-3 py-2 text-sm ${tc.text}`;

  return (
    <section className={`mb-4 rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`} aria-labelledby="extraordinary-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="extraordinary-title" className={`text-base font-semibold ${tc.text}`}>
            {t("payroll.extraordinary.title")}
          </h3>
          <p className={`mt-0.5 text-xs ${tc.textSecondary}`}>{t("payroll.extraordinary.subtitle")}</p>
        </div>
        <p className={`text-xs ${tc.textSecondary}`}>
          {t("payroll.extraordinary.summary", { count: summary.count, unclassified: summary.unclassified })}
        </p>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
          {t("payroll.extraordinary.member")}
          <select className={inputCls} value={memberId} onChange={(e) => setMemberId(e.target.value)}>
            <option value="">{t("payroll.extraordinary.allMembers")}</option>
            {members.map((m) => (
              <option key={m.teamMemberId} value={m.teamMemberId}>
                {m.teamMemberName} ({m.count})
              </option>
            ))}
          </select>
        </label>
        <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
          {t("payroll.extraordinary.from")}
          <input type="date" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className={`flex flex-col gap-1 text-xs ${tc.textSecondary}`}>
          {t("payroll.extraordinary.to")}
          <input type="date" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className={`flex items-center gap-2 pb-1.5 text-xs ${tc.textSecondary}`}>
          <input type="checkbox" checked={includeRegular} onChange={(e) => setIncludeRegular(e.target.checked)} />
          {t("payroll.extraordinary.showRegular")}
        </label>
      </div>

      {members.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {members.map((m) => (
            <li key={m.teamMemberId} className={`rounded-lg border px-3 py-1.5 text-xs ${tc.border} ${tc.textSecondary}`}>
              <span className={`font-medium ${tc.text}`}>{m.teamMemberName}</span>
              {" · "}
              {Object.entries(m.totals)
                .map(([currency, amount]) => formatMoney(amount, currency))
                .join(" + ")}
            </li>
          ))}
        </ul>
      )}

      {error && <p className="mt-3 text-sm text-red-500">{error}</p>}

      {loading ? (
        <p className={`mt-4 text-sm ${tc.textSecondary}`}>{t("payroll.loading")}</p>
      ) : visible.length === 0 ? (
        <p className={`mt-4 text-sm ${tc.textSecondary}`}>{t("payroll.extraordinary.empty")}</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[880px] border-collapse">
            <thead>
              <tr className={`border-b ${tc.border}`}>
                <th className={th}>{t("payroll.fields.date")}</th>
                <th className={th}>{t("payroll.fields.teamMember")}</th>
                <th className={th}>{t("payroll.extraordinary.source")}</th>
                <th className={`${th} text-right`}>{t("payroll.fields.amount")}</th>
                <th className={`${th} text-right`}>{t("payroll.extraordinary.expected")}</th>
                <th className={`${th} text-right`}>{t("payroll.extraordinary.difference")}</th>
                <th className={th}>{t("payroll.extraordinary.reason")}</th>
                <th className={th}>{t("payroll.extraordinary.classification")}</th>
                <th className={th}>{t("payroll.extraordinary.note")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.key} className={`border-b align-top ${tc.border}`}>
                  <td className={`${td} whitespace-nowrap`}>{row.date}</td>
                  <td className={td}>{row.teamMemberName}</td>
                  <td className={td}>
                    {t(`payroll.extraordinary.sources.${row.source}`)}
                    {row.concept && <span className={`block text-xs ${tc.textSecondary}`}>{row.concept}</span>}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right font-medium`}>{formatMoney(row.amount, row.currency)}</td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    {row.expected === null ? "—" : formatMoney(row.expected, row.currency)}
                  </td>
                  <td className={`${td} whitespace-nowrap text-right`}>
                    {row.difference === null ? (
                      "—"
                    ) : (
                      <span className={row.difference > 0 ? "text-amber-600" : "text-sky-600"}>
                        {row.difference > 0 ? "+" : "−"} {formatMoney(Math.abs(row.difference), row.currency)}
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    {row.reasons.map((r) => t(`payroll.extraordinary.reasons.${r}`)).join(" · ")}
                    {row.recurring && (
                      <span className={`block text-xs ${tc.textSecondary}`}>
                        {t("payroll.extraordinary.recurring", { count: row.recurring })}
                      </span>
                    )}
                  </td>
                  <td className={td}>
                    {canReview ? (
                      <select
                        className={inputCls}
                        aria-label={`${t("payroll.extraordinary.classification")}: ${row.teamMemberName} ${row.date}`}
                        value={row.classification ?? ""}
                        disabled={saving === row.key}
                        onChange={(e) => void classify(row, e.target.value)}
                      >
                        <option value="">{t("payroll.extraordinary.unclassified")}</option>
                        {CLASSIFICATIONS.map((c) => (
                          <option key={c} value={c}>
                            {t(`payroll.extraordinary.classifications.${c}`)}
                          </option>
                        ))}
                        {/* Solo un sueldo con otro monto puede volverse el monto habitual de la persona. */}
                        {(row.classification === "regular_amount" || (row.reasons.length === 1 && row.reasons[0] === "amount_differs")) && (
                          <option value="regular_amount">
                            {t("payroll.extraordinary.classifications.regular_amount", { amount: formatMoney(row.amount, row.currency) })}
                          </option>
                        )}
                      </select>
                    ) : row.classification === "regular_amount" ? (
                      t("payroll.extraordinary.classifications.regular_amount", { amount: formatMoney(row.amount, row.currency) })
                    ) : row.classification ? (
                      t(`payroll.extraordinary.classifications.${row.classification}`)
                    ) : (
                      t("payroll.extraordinary.unclassified")
                    )}
                    {/* #1491: un reembolso se carga a un negocio o a un lead del CRM. */}
                    {row.classification === "reimbursement" &&
                      (canReview ? (
                        <div className="mt-1 flex flex-col gap-1">
                          <select
                            className={inputCls}
                            aria-label={`${t("payroll.extraordinary.chargeDeal")}: ${row.teamMemberName} ${row.date}`}
                            value={row.dealId ?? ""}
                            disabled={saving === row.key}
                            onChange={(e) => void assignTarget(row, { dealId: e.target.value || null })}
                          >
                            <option value="">{t("payroll.extraordinary.noDeal")}</option>
                            {deals.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.title}
                              </option>
                            ))}
                          </select>
                          <select
                            className={inputCls}
                            aria-label={`${t("payroll.extraordinary.chargeLead")}: ${row.teamMemberName} ${row.date}`}
                            value={row.leadId ?? ""}
                            disabled={saving === row.key}
                            onChange={(e) => void assignTarget(row, { leadId: e.target.value || null })}
                          >
                            <option value="">{t("payroll.extraordinary.noLead")}</option>
                            {leads.map((l) => (
                              <option key={l.id} value={l.id}>
                                {leadLabel(l)}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        (row.dealId || row.leadId) && (
                          <span className={`block text-xs ${tc.textSecondary}`}>
                            {row.dealId ? `${t("payroll.extraordinary.chargeDeal")}: ${deals.find((d) => d.id === row.dealId)?.title ?? row.dealId}` : `${t("payroll.extraordinary.chargeLead")}: ${row.leadId}`}
                          </span>
                        )
                      ))}
                    {/* #1487: un sueldo con monto distinto pudo ser un aumento: se documenta en el historial. */}
                    {canReview && onRegisterRaise && row.reasons.includes("amount_differs") && row.expected !== null && (
                      <button
                        type="button"
                        className={`mt-1 block text-xs underline ${tc.textSecondary}`}
                        title={t("payroll.extraordinary.registerRaiseHint")}
                        onClick={() => onRegisterRaise(row)}
                      >
                        {t("payroll.extraordinary.registerRaise")}
                      </button>
                    )}
                  </td>
                  <td className={`${td} min-w-[220px]`}>
                    {noteEditable(row) ? (
                      <div className="flex flex-col gap-1">
                        <textarea
                          className={`${inputCls} w-full`}
                          rows={2}
                          aria-label={`${t("payroll.extraordinary.note")}: ${row.teamMemberName} ${row.date}`}
                          placeholder={t("payroll.extraordinary.notePlaceholder")}
                          value={draftOf(row)}
                          disabled={saving === row.key}
                          onChange={(e) => setNoteDrafts((d) => ({ ...d, [row.key]: e.target.value }))}
                        />
                        <button
                          type="button"
                          className={`self-start rounded-md border px-2 py-1 text-xs ${tc.border} ${tc.text} ${tc.buttonHover} disabled:opacity-50`}
                          disabled={saving === row.key || draftOf(row).trim() === (row.note ?? "")}
                          onClick={() => void saveNote(row)}
                        >
                          {t("payroll.extraordinary.saveNote")}
                        </button>
                      </div>
                    ) : row.note ? (
                      <span>{row.note}</span>
                    ) : (
                      <span className={tc.textSecondary}>—</span>
                    )}
                    {(row.noteHistory ?? []).length > 0 && (
                      <details className="mt-1 text-xs">
                        <summary className={`cursor-pointer ${tc.textSecondary}`}>
                          {t("payroll.extraordinary.noteHistory", { count: (row.noteHistory ?? []).length })}
                        </summary>
                        <ul className="mt-1 space-y-1">
                          {[...(row.noteHistory ?? [])].reverse().map((h, i) => (
                            <li key={`${h.at}-${i}`}>
                              <span className={tc.text}>{h.note}</span>
                              <span className={`block ${tc.textSecondary}`}>
                                {t("payroll.extraordinary.noteSavedBy", { name: h.byName || h.by || "—", date: String(h.at).slice(0, 10) })}
                                {h.classification ? ` · ${t(`payroll.extraordinary.classifications.${h.classification}`)}` : ""}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
